import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Typography, useTheme, Drawer, useMediaQuery, IconButton, AppBar, Toolbar } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useAddUserResponseDialogStyles } from '../AddUserResponseDialog/useAddUserResponseDialogStyles';
import CustomDialog from '@ui/components/common/Dialog';
import FamilyFormContent from './FamilyFormContent';
import { EMPTY_PERSON } from './FamilyPersonCard';
import { buildFamilyFestAdminRegistrationBody } from '@application/features/familyFest/build-complete-family-fest-registration-from-form';
import { useAppDispatch } from '@application/store/useStore';
import { fetchFamilyFestAttendees, registerFamilyFestAttendeeAdminAsync } from '@application/features/familyFest/familyFestAttendeesThunk';
const AddFamilyResponseDialog = ({ open, onClose, eventId, paginationModel: _paginationModel, onRefreshAttendees }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const styles = useAddUserResponseDialogStyles();
    const dispatch = useAppDispatch();
    const [isSaving, setIsSaving] = useState(false);
    const [familyIsSaved, setFamilyIsSaved] = useState(false);
    const { control, getValues, setValue, reset, trigger, watch, formState: { errors } } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            persons: [EMPTY_PERSON]
        }
    });
    React.useEffect(() => {
        if (open) {
            reset({ persons: [EMPTY_PERSON], vehicleCount: null });
            setFamilyIsSaved(false);
        }
    }, [open, reset]);
    const scrollToFirstError = () => {
        setTimeout(() => {
            const firstError = document.querySelector('[aria-invalid="true"]');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };
    const refetchAttendees = useCallback(() => {
        if (onRefreshAttendees) {
            onRefreshAttendees();
            return;
        }
        if (!eventId)
            return;
        dispatch(fetchFamilyFestAttendees({
            eventId,
            req: {
                isArchived: false,
                isComingToEvent: true,
                sortBy: 'createdon',
                sortOrder: 'desc'
            }
        }));
    }, [dispatch, eventId, onRefreshAttendees]);
    const submitFamilyFestRegistration = useCallback(async (options) => {
        const closeAfter = options?.closeAfter !== false;
        const values = getValues();
        const persons = values.persons || [];
        const primary = persons[0];
        if (!primary) {
            toast.error(t('familyForm.missingPrimaryPerson'));
            return false;
        }
        const companyId = String(primary.company ?? '').trim();
        const email = String(primary.email ?? '').trim();
        if (!companyId || !email) {
            toast.error(t('familyForm.companyAndEmailRequired'));
            return false;
        }
        if (!eventId) {
            toast.error(t('familyForm.eventRequired'));
            return false;
        }
        const body = buildFamilyFestAdminRegistrationBody(values, []);
        if (primary.ComingWith === 'family' && body.members.length === 0) {
            toast.error(t('familyForm.familymusthaveatleastOneMember'));
            return false;
        }
        if (primary.ComingWith === 'guest' && body.members.length === 0) {
            toast.error(t('familyForm.guestmusthaveexactlyOneMember'));
            return false;
        }
        setIsSaving(true);
        try {
            const cBody = await dispatch(registerFamilyFestAttendeeAdminAsync({ eventId, body: body })).unwrap();
            if (!cBody.isSuccess) {
                const msg = cBody.message?.trim();
                toast.error(msg || t('familyForm.saveAttendeeFailed'));
                return false;
            }
            toast.success(t('events.eventDetails.responsesTab.responseAddedSuccessfully'));
            refetchAttendees();
            if (closeAfter) {
                onClose();
            }
            return true;
        }
        catch {
            toast.error(t('familyForm.registrationFailed'));
            return false;
        }
        finally {
            setIsSaving(false);
        }
    }, [eventId, getValues, onClose, refetchAttendees, t]);
    const handleFamilySave = async () => {
        const persons = getValues('persons') || [];
        const primary = persons[0];
        const comingWith = String(primary?.ComingWith ?? '')
            .toLowerCase()
            .trim();
        const shortFlow = comingWith === 'alone' || primary?.eventChoice === 'false';
        if (shortFlow) {
            const isValid = await trigger('persons.0');
            if (!isValid) {
                scrollToFirstError();
                return;
            }
            const termsOk = await trigger('persons.0.TermsAccepted');
            if (!termsOk) {
                scrollToFirstError();
                return;
            }
            await submitFamilyFestRegistration({ closeAfter: true });
            return;
        }
        const isValid = await trigger();
        if (!isValid) {
            scrollToFirstError();
            return;
        }
        await submitFamilyFestRegistration({ closeAfter: true });
    };
    const handleFamilyAddAnother = async () => {
        const isValid = await trigger();
        if (!isValid) {
            scrollToFirstError();
            return;
        }
        const termsOk = await trigger('persons.0.TermsAccepted');
        if (!termsOk) {
            scrollToFirstError();
            return;
        }
        await submitFamilyFestRegistration({ closeAfter: false })
            .then((success) => {
            if (success) {
                reset({ persons: [EMPTY_PERSON], vehicleCount: null });
            }
        });
        setFamilyIsSaved(false);
    };
    const watchedPrimary = watch('persons.0');
    const comingWithNorm = String(watchedPrimary?.ComingWith ?? '')
        .toLowerCase()
        .trim();
    const skipsTransportStep = comingWithNorm === 'alone' || watchedPrimary?.eventChoice === 'false';
    const isFamilyGuestOrFamilyFlow = comingWithNorm === 'family' ||
        comingWithNorm === 'guest' ||
        comingWithNorm === 'introduction';
    const primarySubmitButtonLabel = isSaving
        ? t('actions.adding')
        : t('actions.save');
    const showFamilyGuestLinear = (comingWithNorm === 'family' ||
        comingWithNorm === 'guest' ||
        comingWithNorm === 'introduction') &&
        watchedPrimary?.eventChoice === 'true';
    const familyFormContent = (_jsx(FamilyFormContent, { control: control, errors: errors, setValue: setValue, isSaved: familyIsSaved, activeStep: 1, onStepChange: () => { } }));
    const actionButtons = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: onClose, disabled: isSaving, children: t('actions.cancel') }), showFamilyGuestLinear && (_jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', onClick: () => void handleFamilyAddAnother(), disabled: isSaving, children: t('actions.addAndNew') })), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', endIcon: _jsx(CheckOutlined, {}), onClick: () => void handleFamilySave(), disabled: isSaving, children: primarySubmitButtonLabel })] }));
    if (isMobile) {
        return (_jsxs(Drawer, { anchor: 'bottom', open: open, onClose: onClose, PaperProps: { sx: styles.mobileDrawerStyle }, children: [_jsx(AppBar, { position: 'sticky', elevation: 0, sx: styles.appBarStyle, children: _jsxs(Toolbar, { sx: styles.mobileToolbarStyle, children: [_jsx(Typography, { variant: 'h6', color: 'text.primary', fontWeight: 600, children: t('events.eventDetails.responsesTab.addNewAttendee') || 'Add New Attendee' }), _jsx(IconButton, { onClick: onClose, size: 'small', sx: { border: 'none' }, children: _jsx(CloseIcon, {}) })] }) }), _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: _jsx(Box, { sx: { px: 2, py: 2 }, children: familyFormContent }) }), _jsxs(Box, { sx: styles.footerBoxStyle, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: onClose, disabled: isSaving, sx: styles.actionButtonStyle, children: t('actions.cancel') }), showFamilyGuestLinear && (_jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', onClick: () => void handleFamilyAddAnother(), disabled: isSaving, children: t('actions.addAndNew') })), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, endIcon: _jsx(CheckOutlined, {}), onClick: () => void handleFamilySave(), disabled: isSaving, sx: styles.actionButtonStyle, children: primarySubmitButtonLabel })] })] }));
    }
    return (_jsx(CustomDialog, { backgroundVariant: 'white', titleWithIcon: t('events.eventDetails.responsesTab.addNewAttendee') || 'Add New Attendee', open: open, onClose: onClose, maxWidth: 'md', titleIcon: '/assets/images/add-person.png', actions: actionButtons, children: _jsx(Box, { sx: styles.dialogContentStyle, children: _jsx(Box, { sx: { mt: 2, px: 2 }, children: familyFormContent }) }) }));
};
export default AddFamilyResponseDialog;
